import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/Toast';

function TestDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [test, setTest] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [subTotalPages, setSubTotalPages] = useState(1);
  const [subTotal, setSubTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState('details');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const navigate = useNavigate();
  const { admin, token, logout, loading: authLoading } = useAuth();
  const { notify } = useToast();

  // Read state from URL params
  const subPage = parseInt(searchParams.get('page')) || 1;
  const sortBy = searchParams.get('sortBy') || 'submittedAt';
  const order = searchParams.get('order') || 'desc';
  const statusFilter = searchParams.get('status') || '';
  const flagsFilter = searchParams.get('flags') || '';
  const search = searchParams.get('search') || '';

  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) next.delete(k);
        else next.set(k, v);
      });
      return next;
    });
  }, [setSearchParams]);

  const handleSort = (field) => {
    if (sortBy === field) {
      updateParams({ order: order === 'asc' ? 'desc' : 'asc', page: 1 });
    } else {
      updateParams({ sortBy: field, order: 'desc', page: 1 });
    }
  };

  const sortArrow = (field) => {
    if (sortBy !== field) return ' \u2195';
    return order === 'asc' ? ' \u2191' : ' \u2193';
  };

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    const fetchTest = async () => {
      if (!token) return;
      try {
        const testRes = await axios.get(`/api/tests/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTest(testRes.data.test || testRes.data);
      } catch (err) {
        setError('Failed to load test details');
      }
    };
    if (token) fetchTest();
  }, [id, token]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!token) return;
      try {
        const params = new URLSearchParams({ page: subPage, limit: 50, sortBy, order });
        if (statusFilter) params.set('status', statusFilter);
        if (flagsFilter) params.set('flags', flagsFilter);
        if (search) params.set('search', search);
        const subRes = await axios.get(`/api/submissions/test/${id}?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubmissions(subRes.data.submissions || subRes.data || []);
        setSubTotalPages(subRes.data.totalPages || 1);
        setSubTotal(subRes.data.total || 0);
      } catch (err) {
        setError('Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchSubmissions();
  }, [id, token, subPage, sortBy, order, statusFilter, flagsFilter, search]);

  const copyLink = async () => {
    if (!test?.shareableSlug) return;
    const link = `${window.location.origin}/exam/${test.shareableSlug}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    notify('Link copied to clipboard', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="admin-layout">
        <aside className="sidebar">
          <div className="sidebar-header"><h2>Exam System</h2></div>
        </aside>
        <main className="admin-main">
          <div className="loading-spinner">Loading...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Exam System</h2>
        </div>
        <nav className="sidebar-nav">
          <Link to="/admin/dashboard" className="nav-link">Dashboard</Link>
          <Link to="/admin/create-test" className="nav-link">Create Test</Link>
          <Link to="/admin/candidates" className="nav-link">Candidates</Link>
        </nav>
        <div className="sidebar-footer">
          <div className="admin-info">
            <span className="admin-name">{admin?.name || admin?.email}</span>
          </div>
          <button className="btn btn-logout" onClick={logout}>Logout</button>
        </div>
      </aside>

      <main className="admin-main">
        {error && <div className="error-message">{error}</div>}

        {test && (
          <>
            <div className="admin-header">
              <div>
                <Link to="/admin/dashboard" className="back-link">&larr; Back to Dashboard</Link>
                <h1>{test.title}</h1>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-primary" onClick={copyLink}>
                  {copied ? 'Copied!' : 'Copy Shareable Link'}
                </button>
                <button
                  className={`btn ${test.isActive ? 'btn-danger' : 'btn-primary'}`}
                  onClick={async () => {
                    try {
                      const res = await axios.put(`/api/tests/${id}`, {
                        isActive: !test.isActive,
                      }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setTest(res.data.test || res.data);
                      notify(test.isActive ? 'Test deactivated' : 'Test activated', 'success');
                    } catch (err) {
                      notify('Failed to update test status', 'error');
                    }
                  }}
                >
                  {test.isActive ? 'Deactivate Test' : 'Activate Test'}
                </button>
              </div>
            </div>

            <div className="link-box mb-1">
              <code>{window.location.origin}/exam/{test.shareableSlug}</code>
              {!test.isActive && (
                <span style={{ marginLeft: '0.75rem', color: '#e74c3c', fontWeight: 'bold', fontSize: '0.85rem' }}>
                  (INACTIVE)
                </span>
              )}
            </div>

            <div className="tab-switch mb-1">
              <button
                className={`tab-btn ${activeSection === 'details' ? 'active' : ''}`}
                onClick={() => setActiveSection('details')}
              >
                Test Details
              </button>
              <button
                className={`tab-btn ${activeSection === 'submissions' ? 'active' : ''}`}
                onClick={() => setActiveSection('submissions')}
              >
                Submissions ({subTotal})
              </button>
            </div>

            {activeSection === 'details' && (
              <div className="detail-sections">
                <div className="detail-card">
                  <h3>Test Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Duration</span>
                      <span className="detail-value">{test.duration} minutes</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Passing Percentage</span>
                      <span className="detail-value">{test.passingPercentage}%</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Question Bank Size</span>
                      <span className="detail-value">{test.questions?.length || 0}</span>
                    </div>
                    {test.questionsPerCandidate > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Questions Per Candidate</span>
                        <span className="detail-value">{test.questionsPerCandidate} (randomized)</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Total Points</span>
                      <span className="detail-value">
                        {test.questions?.reduce((sum, q) => sum + (q.points || 1), 0) || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {test.jobDescription && (
                  <div className="detail-card">
                    <h3>Job Description</h3>
                    <p className="jd-text">{test.jobDescription}</p>
                  </div>
                )}

                <div className="detail-card">
                  <h3>Questions &amp; Expected Answers</h3>
                  {test.questions?.map((q, i) => (
                    <div key={i} className="question-review">
                      <div className="question-review-header">
                        <strong>Q{i + 1}.</strong>
                        <span className="points-badge">{q.points || 1} pts</span>
                      </div>
                      <p className="question-text">{q.question}</p>
                      <div className="expected-answer">
                        <span className="expected-label">Expected Answer:</span>
                        <p>{q.expectedAnswer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeSection === 'submissions' && (
              <div className="detail-card">
                {/* Search & Filter Bar */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <form onSubmit={(e) => { e.preventDefault(); updateParams({ search: searchInput, page: 1 }); }} style={{ display: 'flex', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem', flex: 1 }}
                    />
                    <button type="submit" className="btn btn-primary btn-sm">Search</button>
                    {search && (
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => { setSearchInput(''); updateParams({ search: '', page: 1 }); }}>Clear</button>
                    )}
                  </form>
                  <select
                    value={statusFilter}
                    onChange={(e) => updateParams({ status: e.target.value, page: 1 })}
                    style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                  >
                    <option value="">All Status</option>
                    <option value="passed">Passed</option>
                    <option value="failed">Failed</option>
                  </select>
                  <select
                    value={flagsFilter}
                    onChange={(e) => updateParams({ flags: e.target.value, page: 1 })}
                    style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                  >
                    <option value="">All Integrity</option>
                    <option value="flagged">Has Flags</option>
                    <option value="clean">Clean</option>
                  </select>
                </div>

                {submissions.length === 0 && !search && !statusFilter && !flagsFilter ? (
                  <div className="empty-state">
                    <h3>No submissions yet</h3>
                    <p>Share the test link with candidates to receive submissions.</p>
                  </div>
                ) : submissions.length === 0 ? (
                  <div className="empty-state">
                    <h3>No results</h3>
                    <p>No submissions match your search or filter criteria.</p>
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Candidate</th>
                          <th>Email</th>
                          <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('totalScore')}>Score{sortArrow('totalScore')}</th>
                          <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('percentage')}>Percentage{sortArrow('percentage')}</th>
                          <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('passed')}>Status{sortArrow('passed')}</th>
                          <th>Cheat Flags</th>
                          <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('submittedAt')}>Submitted{sortArrow('submittedAt')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.map((sub) => (
                          <tr
                            key={sub._id}
                            className="clickable-row"
                            onClick={() => {
                              const candidateId = sub.candidate?._id || sub.candidate;
                              if (candidateId) navigate(`/admin/candidate/${candidateId}`);
                            }}
                          >
                            <td>{sub.candidate?.name || sub.candidate?.email || 'N/A'}</td>
                            <td>{sub.candidate?.email || 'N/A'}</td>
                            <td>{sub.scoringStatus === 'completed' ? `${Number(sub.totalScore || 0).toFixed(1)}/${sub.maxScore || 0}` : '...'}</td>
                            <td>{sub.scoringStatus === 'completed' ? `${Math.round(sub.percentage)}%` : '...'}</td>
                            <td>
                              {sub.scoringStatus === 'completed' ? (
                                <span className={`status-badge ${sub.passed ? 'status-pass' : 'status-fail'}`}>
                                  {sub.passed ? 'Passed' : 'Failed'}
                                </span>
                              ) : (
                                <span className="status-badge" style={{ background: '#f59e0b', color: '#fff' }}>
                                  {sub.scoringStatus === 'failed' ? 'Scoring Failed' : 'Scoring...'}
                                </span>
                              )}
                            </td>
                            <td>
                              {sub.cheatFlags?.length > 0 ? (
                                <span className="cheat-flag-count">{sub.cheatFlags.length} flags</span>
                              ) : (
                                <span className="no-flags">None</span>
                              )}
                            </td>
                            <td>{sub.createdAt ? new Date(sub.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {subTotalPages > 1 && (
                      <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => updateParams({ page: Math.max(1, subPage - 1) })}
                          disabled={subPage <= 1}
                        >
                          &larr; Previous
                        </button>
                        <span style={{ fontSize: '0.9rem' }}>
                          Page {subPage} of {subTotalPages} ({subTotal} total)
                        </span>
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => updateParams({ page: Math.min(subTotalPages, subPage + 1) })}
                          disabled={subPage >= subTotalPages}
                        >
                          Next &rarr;
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default TestDetail;
